/**
 * 类型化 API 客户端。
 * - access token 仅存内存（不落 localStorage，防 XSS 窃取）
 * - 401 时自动用 httpOnly refresh cookie 换新令牌并重放原请求（每请求最多一次）
 * - 统一解包 { code, message, data } 成功信封与 { error } 错误信封
 */

const BASE = "/api";

import type {
  ArchiveDTO,
  ArchiveGroup,
  CurrentUser,
  DashboardStats,
  FollowReportDTO,
  MeetingDetectionDTO,
  MeetingScheduleDTO,
  OrgDeptNode,
  OrgUser,
  QuickCreateInput,
  QuickCreateResult,
  TokenPair,
} from "./types";

let accessToken: string | null = null;
const tokenListeners = new Set<(token: string | null) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of tokenListeners) listener(token);
}

export function onTokenChange(listener: (token: string | null) => void): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** 是否已重试过（内部使用，防刷新死循环） */
  retried?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.pathname + url.search;
}

async function parseEnvelope(res: Response): Promise<{ data: unknown; error?: { code: string; message: string } }> {
  const json = (await res.json().catch(() => null)) as {
    code?: number;
    data?: unknown;
    error?: { code: string; message: string };
  } | null;
  return { data: json?.data, error: json?.error };
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, retried = false } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: "include", // refresh cookie 随请求发送
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  // 令牌过期：静默刷新一次并重放
  if (res.status === 401 && !retried && accessToken && !path.startsWith("/auth/")) {
    try {
      await api.auth.refresh();
      return request<T>(path, { ...options, retried: true });
    } catch {
      setAccessToken(null);
      throw new ApiError(401, "UNAUTHORIZED", "登录已过期，请重新登录");
    }
  }

  const { data, error } = await parseEnvelope(res);

  if (!res.ok || (error && res.status >= 400)) {
    throw new ApiError(res.status, error?.code ?? "UNKNOWN", error?.message ?? `请求失败（${res.status}）`);
  }
  return data as T;
}

export const api = {
  auth: {
    /** 构造企业微信授权地址（mock 模式返回演示回调地址）
     *  mode=qr → Web 扫码登录页（普通浏览器展示二维码，企微 App 扫码确认） */
    wechatUrl: (mode?: "qr") =>
      request<{ url: string; mock: boolean }>("/auth/wechat/url", {
        query: mode ? { mode } : undefined,
      }),

    /** 授权回调换令牌 */
    wechatCallback: (params: { code: string; state: string }) =>
      request<TokenPair>("/auth/wechat/callback", { method: "GET", query: params }),

    /** 本地开发登录（演示账号） */
    devLogin: (wechatUserId: string) =>
      request<TokenPair>("/auth/dev-login", { method: "POST", body: { wechatUserId } }),

    /** 刷新令牌对 */
    refresh: () => request<TokenPair>("/auth/refresh", { method: "POST" }),

    logout: () => request<null>("/auth/logout", { method: "POST" }),

    me: () => request<CurrentUser>("/auth/me"),
  },

  meetings: {
    stats: (date?: string) => request<DashboardStats>("/meetings/stats", { query: { date } }),

    detections: (params: { type?: string; deptId?: string; date?: string }) =>
      request<MeetingDetectionDTO[]>("/meetings/detections", { query: params }),

    schedules: () => request<MeetingScheduleDTO[]>("/meetings/schedules"),

    overrideDetection: (id: string, status: string) =>
      request<MeetingDetectionDTO>(`/meetings/detections/${id}`, { method: "PATCH", body: { status } }),

    /** 一键建会：按组织架构创建周期会议（管理员） */
    quickCreate: (body: QuickCreateInput) =>
      request<QuickCreateResult>("/meetings/quick-create", { method: "POST", body }),
  },

  org: {
    /** 组织架构部门树（拍平节点，前端组装） */
    departments: () => request<OrgDeptNode[]>("/org/departments"),

    /** 部门直属成员 */
    users: (deptId: string) => request<OrgUser[]>("/org/users", { query: { deptId } }),
  },

  archives: {
    list: (params: { type?: string; q?: string; limit?: number }) =>
      request<ArchiveDTO[]>("/archives", { query: params }),

    grouped: (params: { type?: string; q?: string; limit?: number }) =>
      request<ArchiveGroup[]>("/archives", { query: { ...params, grouped: true } }),

    counts: () => request<Record<string, number>>("/archives/counts"),

    /** 纪要详情（含正文 content） */
    get: (id: string) => request<ArchiveDTO>(`/archives/${id}`),

    /** 人工编辑纪要正文（保存后自动同步微盘） */
    update: (id: string, content: string) =>
      request<ArchiveDTO>(`/archives/${id}`, { method: "PATCH", body: { content } }),

    /** 纪要正文（markdown，优先读库中正文，其次回退微盘） */
    content: (id: string) => request<{ content: string }>(`/archives/${id}/content`),

    /** 下载纪要（带令牌拉取后触发浏览器保存；未同步微盘时抛出明确错误） */
    download: async (id: string, fileName: string) => {
      const res = await fetch(buildUrl(`/archives/${id}/download`), {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        credentials: "include",
      });
      if (!res.ok) {
        const json = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new ApiError(res.status, "DOWNLOAD_FAILED", json?.error?.message ?? `下载失败（${res.status}）`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName.endsWith(".md") ? fileName : `${fileName}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  },

  followups: {
    depts: () => request<{ id: string; name: string }[]>("/followups/depts"),

    get: (deptId: string, period?: string) =>
      request<FollowReportDTO>(`/followups/${deptId}`, { query: { period } }),

    generate: (deptId: string, period?: string) =>
      request<FollowReportDTO>(`/followups/${deptId}/generate`, { method: "POST", query: { period } }),
  },
};
